import React, { useState } from 'react'
import { Button } from './Button'
import { submitFeedback, type SubmitFeedbackRequest } from '../../services/api'

interface ContactFormProps {
  className?: string
}

type FeedbackType = 'review' | 'feedback' | 'bug' | 'feature'

export function ContactForm({ className = '' }: ContactFormProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('feedback')
  const [message, setMessage] = useState('')
  const [rating, setRating] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (message.trim().length < 10) {
      setSubmitError('Please provide at least 10 characters of feedback')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const request: SubmitFeedbackRequest = {
        feedbackType,
        message: message.trim(),
      }

      if (feedbackType === 'review' && rating > 0) {
        request.rating = rating
      }

      await submitFeedback(request)

      setSubmitSuccess(true)
      setMessage('')
      setRating(0)
      setFeedbackType('feedback')

      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000)
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const feedbackTypes: { id: FeedbackType; label: string; icon: string; description: string }[] = [
    {
      id: 'review',
      label: 'Review',
      icon: '⭐',
      description: 'Share your experience',
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: '💬',
      description: 'General thoughts',
    },
    {
      id: 'bug',
      label: 'Bug Report',
      icon: '🐛',
      description: 'Something wrong?',
    },
    {
      id: 'feature',
      label: 'Feature',
      icon: '💡',
      description: 'Suggest improvements',
    },
  ]

  return (
    <div className={className}>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">📧</span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Contact Us</h2>
            <p className="text-sm text-gray-600">We'd love to hear from you</p>
          </div>
        </div>

        {submitSuccess && (
          <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-semibold text-success-800">Thank you for your feedback!</p>
                <p className="text-sm text-success-700 mt-1">
                  We appreciate you taking the time to help us improve the Japanese Learning Platform.
                </p>
              </div>
            </div>
          </div>
        )}

        {submitError && (
          <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-xl">❌</span>
              <div>
                <p className="font-semibold text-error-800">Error</p>
                <p className="text-sm text-error-700 mt-1">{submitError}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Feedback Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              What would you like to share?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {feedbackTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFeedbackType(type.id)}
                  className={`p-3 border rounded-lg text-center transition ${
                    feedbackType === type.id
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl mb-1">{type.icon}</div>
                  <div className="text-sm font-semibold">{type.label}</div>
                  <div className="text-xs opacity-75 mt-1">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Star Rating (for reviews) */}
          {feedbackType === 'review' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                How would you rate your experience?
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="text-4xl transition-transform hover:scale-110 focus:outline-none"
                  >
                    {star <= rating ? '⭐' : '☆'}
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {rating === 1 && "We appreciate your feedback and will work to improve"}
                  {rating === 2 && "Thank you for your honest feedback"}
                  {rating === 3 && "Good to know, we'll keep improving"}
                  {rating === 4 && "Great! We're glad you're enjoying it"}
                  {rating === 5 && "Awesome! Thank you for your support"}
                </p>
              )}
            </div>
          )}

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Your message <span className="text-error-600">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={5000}
              required
              placeholder={
                feedbackType === 'review'
                  ? "Tell us about your experience with the platform..."
                  : feedbackType === 'bug'
                  ? "Please describe the bug and steps to reproduce it..."
                  : feedbackType === 'feature'
                  ? "Describe the feature or improvement you'd like to see..."
                  : "Share your thoughts with us..."
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">Minimum 10 characters</p>
              <p className="text-xs text-gray-500">
                {message.length} / 5000
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || message.trim().length < 10}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </form>

        {/* Privacy Note */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Your feedback will be sent to our team. We typically respond within 24-48 hours.
            <br />
            Your email address (if provided in your profile) will be included so we can follow up if needed.
          </p>
        </div>
      </div>
    </div>
  )
}
